import { Divider, Drawer } from "@mui/material";
import { FC, KeyboardEvent, MouseEvent, useContext, useEffect, useState } from "react";
import OrderDetailsLineItemFlyout from "components/OrderDetailsLineItem/OrderDetailsLineItemFlyout.tsx";
import { cartActions } from "features/reducers/replacementOrder/cart.ts";
import FlyoutHandler from "components/Replacements/FlyoutHandler.tsx";
import { models } from "types/api/viewModels.ts";
import { useDispatch } from "react-redux";
import { GlobalInfo, LineItemGridWrapper } from "components/OrderDetailsLineItem/OrderDetailsLineItem.styles.ts";
import { MultiplierText } from "constants/text.ts";
import { checkAcknowledgementEligibility, getSingleOrderGlobal } from "pages/OrderPages/utils/OrderDetailUtil.ts";

import { getAttributes, getSubheaderAttributes } from "components/NewOrders/utils/NewOrderUtils.tsx";
import ConfigurationsContext from "context/configurations/ConfigurationsContext.ts";
import { AcknowledgementPriceFormat, OrderType } from "types/declarations/enums.ts";
import { useGetPermissionsQuery } from "features/api/permissionsApi.ts";
import { OrderDetailsLineItemRow } from "components/OrderDetailsLineItem/OrderDetailsLineItemGrid/OrderDetailsLineItemRow.tsx";
import { isMerchOrCampaignOrder } from "utils/order.ts";

export type LineItemGridProps = {
	orderDetail: models["OrderDetailViewModel"] | null | undefined;
	variant: "detail" | "replacement";
	billTo?: models["BillToViewModel"] | null;
};

const OrderDetailsLineItemGrid: FC<LineItemGridProps> = (props) => {
	const { configurationsData } = useContext(ConfigurationsContext);
	const { data: permissionsData } = useGetPermissionsQuery();
	const dispatch = useDispatch();

	const { orderDetail, variant, billTo } = props;
	const isMerchOrder = orderDetail?.orderType === OrderType.MERCH;
	const usesMerchOrCampaignLayout = isMerchOrCampaignOrder(orderDetail?.orderType);
	const effectiveAckPermission = isMerchOrder
		? AcknowledgementPriceFormat.LIST
		: permissionsData?.acknowledgementPriceFormat;
	const { isAcknowledgementEligible: isAcknowledgement } = checkAcknowledgementEligibility(
		orderDetail,
		configurationsData,
		effectiveAckPermission
	);
	const isCustomColor = orderDetail?.orderType === OrderType.CUSTOM_COLOR;
	const [showDrawer, setShowDrawer] = useState(false);
	const [selectedLineItem, setSelectedLineItem] = useState<models["LineItemViewModel"] | null | undefined>();
	const [selectedGlobalInformation, setSelectedGlobalInformation] = useState<
		models["GlobalsViewModel"] | null | undefined
	>();
	const [showReplacement, setShowReplacement] = useState(false);
	const [showShoppingCart, setShowShoppingCart] = useState(false);

	useEffect(() => {
		if (showReplacement) {
			setShowReplacement(false);
		}
		if (showShoppingCart) {
			setShowShoppingCart(false);
		}
	}, [showReplacement, showShoppingCart]);

	const toggleDrawer = (open: boolean) => (event: KeyboardEvent | MouseEvent) => {
		if (event.type === "keydown") {
			const key = (event as KeyboardEvent).key;
			if (key === "Tab" || key === "Shift") {
				return;
			}
		}

		setShowDrawer(open);
		dispatch(cartActions.cancelEdit());
	};

	const handleOnClick = (
		lineItem: models["LineItemViewModel"] | null,
		configuration?: models["OrderConfigurationViewModel"] | null
	) => {
		lineItem?.id && dispatch(cartActions.startEdit(lineItem.id));
		setSelectedLineItem(lineItem);
		configuration && setSelectedGlobalInformation(getSingleOrderGlobal({ configurations: [configuration] }));
		if (variant === "replacement") {
			setShowReplacement(true);
		} else {
			setShowDrawer(true);
		}
	};

	if (!orderDetail) return null;

	return (
		<div>
			<LineItemGridWrapper>
				{orderDetail.configurations?.map((configuration, index, arr) => (
					<>
						{index > 0 && arr.length !== 1 && <Divider sx={{ marginTop: "8px" }} />}
						{!usesMerchOrCampaignLayout && (
							<GlobalInfo data-testid="order-details-configuration-info">
								<div>
									<div>{getAttributes(configuration)}</div>
									{Boolean(
										(effectiveAckPermission === AcknowledgementPriceFormat.NET_MULTIPLIER ||
											billTo?.cashInAdvance) &&
											configuration.multiplier &&
											configuration.multiplier !== 1
									) && (
										<div>
											{MultiplierText} {configuration.multiplier}
										</div>
									)}
								</div>
								<div>{getSubheaderAttributes(configuration)}</div>
							</GlobalInfo>
						)}
						{configuration.lineItems?.map((item) => (
							<OrderDetailsLineItemRow
								key={`${item.sku}-${item.lineItemNumber}`}
								item={item}
								configuration={configuration}
								orderDetail={orderDetail}
								variant={variant}
								isAcknowledgement={isAcknowledgement}
								ackPermission={effectiveAckPermission}
								ackPermissionOverride={isMerchOrder ? AcknowledgementPriceFormat.LIST : undefined}
								handleOnClick={handleOnClick}
								disableDrawer={usesMerchOrCampaignLayout}
								billTo={billTo}
							/>
						))}
					</>
				))}
			</LineItemGridWrapper>
			<Divider />
			{variant === "replacement" ? (
				<FlyoutHandler
					showButtons
					lineItemInformation={selectedLineItem}
					globalInformation={selectedGlobalInformation}
					showReplacementTrigger={showReplacement}
				/>
			) : (
				<Drawer
					anchor="right"
					open={showDrawer}
					onClose={toggleDrawer(false)}
				>
					<OrderDetailsLineItemFlyout
						isCustomColor={isCustomColor}
						closeDrawer={toggleDrawer(false)}
						lineItemInformation={selectedLineItem}
						globalInformation={selectedGlobalInformation}
						orderDetail={orderDetail}
					/>
				</Drawer>
			)}
		</div>
	);
};

export default OrderDetailsLineItemGrid;
